// Translation Store - Manages i18n locale state
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

export const useTranslationStore = defineStore('translation', () => {
  // Available locales in the application
  const availableLocales = [
    { code: 'en-US', name: 'English', flag: '🇺🇸' },
    { code: 'et-EE', name: 'Eesti', flag: '🇪🇪' },
    { code: 'ru-RU', name: 'Русский', flag: '🇷🇺' }
  ];

  // Current locale - initialized from localStorage or default to en-US
  const currentLocale = ref<string>(
    localStorage.getItem('locale') || 'en-US'
  );

  /**
   * Change the application locale
   * @param newLocale - The locale code to switch to (e.g., 'en-US', 'et-EE')
   */
  const changeLocale = (newLocale: string): void => {
    if (!availableLocales.some(l => l.code === newLocale)) {
      console.warn(`Locale ${newLocale} not available, staying with ${currentLocale.value}`);
      return;
    }

    currentLocale.value = newLocale;
    localStorage.setItem('locale', newLocale);

    // Update i18n locale if available
    const { locale } = useI18n({ useScope: 'global' });
    locale.value = newLocale;
  };

  /**
   * Get the display name for a locale code
   */
  const getLocaleName = (code: string): string => {
    return availableLocales.find(l => l.code === code)?.name || code;
  };

  /**
   * Get the flag emoji for a locale code
   */
  const getLocaleFlag = (code: string): string => {
    return availableLocales.find(l => l.code === code)?.flag || '🌐';
  };

  return {
    currentLocale,
    availableLocales,
    changeLocale,
    getLocaleName,
    getLocaleFlag
  };
});
